import { Component, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthenticationService } from '@app/core/services/authentication.service';
import { CheckoutService } from '@app/core/services/checkout.service';
import { AlertController, IonInput, Platform, ToastController } from '@ionic/angular';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { OrderDTO, OrderItemDTO, TicketPriceDTO, TicketPriceService, VehicleTypeDTO, VehicleTypeService } from 'ticket-sdk';
import { Plugins, Capacitor } from '@capacitor/core';
import { TicketUtilityService } from '@app/core/services/ticket-utility.service';
import { LatestPurchasesService } from '@app/core/services/latest-purchases.service';
import { SimpleOrderItem } from '@app/shared/interfaces/simple-order-item.interface';

let Keyboard;
if (Capacitor.platform !== 'web') {
  Keyboard = Plugins.Keyboard;
}

@Component({
  selector: 'app-buy-ticket-form',
  templateUrl: './buy-ticket-form.component.html',
  styleUrls: ['./buy-ticket-form.component.scss'],
})
export class BuyTicketFormComponent implements OnInit, OnDestroy {
  @ViewChild('licensePlateNumberInput') licensePlateNumberInput: IonInput;

  @Input()
  isMobile: boolean;

  @Input()
  isAuthenticated: Observable<boolean>;

  @Input()
  submitTicket: Observable<void>;

  private onDestroy = new Subject();
  ticketGraphicPadding: number = 45;
  
  popoverOptions = {
    cssClass: 'wide-popover'
  }

  ticketPurchaseForm: UntypedFormGroup;
  showNotLoggedInInfoCard: boolean = true;
  ticketPrices: TicketPriceDTO[] = [];
  vehicleTypes: VehicleTypeDTO[] = [];

  constructor(
    private authenticationService: AuthenticationService, 
    private formBuilder: UntypedFormBuilder,
    private ticketPriceService: TicketPriceService,
    private vehicleTypeService: VehicleTypeService,
    private checkoutService: CheckoutService,
    private ticketUtilityService: TicketUtilityService,
    private latestPurchasesService: LatestPurchasesService,
    private alertController: AlertController,
    private router: Router,
    private toastController: ToastController,
    private platform: Platform,
    private zone: NgZone
  ) { }

  ngOnInit() {
    this.ticketPurchaseForm = this.formBuilder.group({
      vehicleType: [null, Validators.required],
      ticketPrice: [null, Validators.required],
      licensePlateNumber: ['', [
        Validators.required, 
        Validators.pattern('^([A-Za-z0-9åÅäÄöÖüÜ ])+$'),
        Validators.maxLength(10),
        Validators.minLength(2)
      ]],
    });

    this.isAuthenticated = this.authenticationService.authenticationState;

    this.vehicleTypeService.getVehicleTypes()
    .pipe(take(1))
    .subscribe(
      vehicleTypes => this.vehicleTypes = vehicleTypes,
      error => {
        if (error.status === 0) {
          this.presentFetchDataErrorToast('Kontrollera din internetanslutning och försök igen');
        }
        else {
          this.presentFetchDataErrorToast();
        }
      }
    );
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
  }

  compareVehicleTypeById(v1: VehicleTypeDTO, v2: VehicleTypeDTO) {
    return v1 && v2 ? v1.vehicleTypeId === v2.vehicleTypeId : v1 === v2;
  }

  compareTicketPriceById(t1: TicketPriceDTO, t2: TicketPriceDTO) {
    return t1 && t2 ? t1.ticketPriceId === t2.ticketPriceId : t1 === t2;
  }

  async confirmLicensePlateNumber(licensePlateNumber: string): Promise<boolean> {
    return new Promise(async resolve => {
      let latestOrderItems: OrderItemDTO[] = this.latestPurchasesService.getLatestPurchases().value.flatMap(simpleOrder => [
        ...simpleOrder.simpleOrderItems
      ]);
  
      if (! latestOrderItems.map(orderItem => orderItem.licensePlateNumber).includes(licensePlateNumber)) {
        const alert = await this.alertController.create({
          cssClass: 'confirm-lpn-alert',
          header: 'Bekräfta reg.nr: ',
          subHeader: licensePlateNumber,
          message: 'Har du skrivit in rätt registreringsnummer?',
          buttons: [
            {
              cssClass: 'secondary',
              text: 'Nej',
              handler: () => {
                resolve(false);
              }
            }, {
              text: 'Ja',
              handler: () => {
                resolve(true);
              }
            }
          ]
        });
    
        await alert.present();
      }
      else {
        resolve(true);
      }
    })
  }

  createOrder(): OrderDTO {
    const formValue = this.ticketPurchaseForm.value;

    const order: OrderDTO = {
      orderId: null,
      paymentMethod: null,
      paymentStatus: "NOT_STARTED",
      purchasingChannel: "APP",
      orderItems: [
        {
          orderItemId: null,
          vehicleType: formValue.vehicleType,
          ticketType: formValue.ticketPrice.ticketType,
          licensePlateNumber: formValue.licensePlateNumber,
          originalQuantity: formValue.ticketPrice.quantity,
          price: formValue.ticketPrice.price,
          vat: formValue.vehicleType.vat
        }
      ],
      termsUrl: "https://www.relyit.se",
      checkoutUrl: "https://www.relyit.se",
      confirmationUrl: "https://www.relyit.se"
    };

    return order;
  }

  dismissNotLoggedInInfoCard() {
    this.showNotLoggedInInfoCard = false;
  }

  getTicketPriceString(ticketPrice: TicketPriceDTO) {
    let ticketPriceString: string = ticketPrice.ticketType.description;

    if (ticketPrice.ticketType.name === 'PERIOD_TICKET') {
      ticketPriceString += ` ${ticketPrice.ticketType.validDays}-dagar`
    }
    else if (ticketPrice.ticketType.name === 'MULTI_TICKET') {
      ticketPriceString += ` ${ticketPrice.quantity} resor`
    }

    ticketPriceString += ` ${(ticketPrice.price)} kr`;

    return ticketPriceString;
  }

  goToCheckout() {
    this.router.navigate(['checkout']);
  }

  onSubmit() {
    if (this.ticketPurchaseForm.valid) {
      const formValue = this.ticketPurchaseForm.value;

      const transformedLincensePlateNumber = this.ticketUtilityService.transformLicensePlateNumber(formValue.licensePlateNumber);

      this.confirmLicensePlateNumber(transformedLincensePlateNumber).then(confirmed => {
        if (confirmed) {
          const simpleOrderItem: SimpleOrderItem = {
            ticketPrice: formValue.ticketPrice,
            licensePlateNumber: transformedLincensePlateNumber,
            price: formValue.ticketPrice.price
          }
    
          if (this.platform.is('capacitor')) {
            Keyboard.hide();
          }
          
          this.checkoutService.addOrderItem(simpleOrderItem);
          this.ticketPurchaseForm.reset();
          this.router.navigateByUrl('checkout');
        }
        else {
          this.ticketPurchaseForm.get('licensePlateNumber').setValue('');

          setTimeout(() => {
            this.zone.run(()=>{
              this.licensePlateNumberInput.setFocus();
            })
          }, 750);
        }
      });
    }
    else {
      let errorMessage;
      let licensePlateNumberInput = this.ticketPurchaseForm.get('licensePlateNumber');

      if (licensePlateNumberInput.errors?.pattern) {
        errorMessage = 'Registreringsnumret får inte innehålla några andra tecken än A-Ö och 0-9';
      }
      else if (licensePlateNumberInput.errors?.minlength) {
        errorMessage = 'Registreringsnumret måste innehålla minst 2 tecken';
      }
      else if (licensePlateNumberInput.errors?.maxlength) {
        errorMessage = 'Registreringsnumret får max innehålla 10 tecken';
      }
      else {
        errorMessage = 'Alla fält måste fyllas i';
      }
      this.presentInvalidFormToast(errorMessage);
    }
  }

  licensePlateNumberChange(event) {
    let licensePlateNumber: string = this.ticketPurchaseForm.get('licensePlateNumber').value;
    if (!! licensePlateNumber && ! this.platform.is('android')) {
      this.ticketPurchaseForm.setValue({
        ...this.ticketPurchaseForm.value, 
        licensePlateNumber: licensePlateNumber.toLocaleUpperCase()
      });
    }
  }

  async presentInvalidFormToast(message: string) {
    const toast = await this.toastController.create({
      message: message,
      color: 'danger',
      position: 'top',
      duration: 5000
    });
    toast.present();
  }

  async presentFetchDataErrorToast(message?: string) {
    message = message || 'Ett okänt fel uppstod. Prova igen om en liten stund';

    const toast = await this.toastController.create({
      message,
      color: 'danger',
      position: 'top',
      duration: 5000
    });
    toast.present();
  }

  ticketPriceChange(event) {
    setTimeout(() => {
      this.zone.run(()=>{
        this.licensePlateNumberInput.setFocus();
      })
    }, 750);
  }

  updateTicketPrices(ticketPrices: TicketPriceDTO[]) {
    this.ticketPrices = ticketPrices;
    this.ticketPurchaseForm.get('ticketPrice').setValue(null);
  }

  vehicleTypeChange(event) {
    let vehicleType = this.ticketPurchaseForm.get('vehicleType').value;
    
    if (vehicleType) {
      this.isAuthenticated.pipe(take(1)).subscribe(isAuthenticated => {
        if (isAuthenticated) {
          this.ticketPriceService.getTicketPricesByVehicleType(vehicleType.vehicleTypeId)
          .pipe(take(1))
          .subscribe(
            ticketPrices => this.updateTicketPrices(ticketPrices),
            error => this.presentFetchDataErrorToast()
          )
        }
        else {
          this.ticketPriceService.getSingleTicketPriceByVehicleType(vehicleType.vehicleTypeId)
          .pipe(take(1))
          .subscribe(
            ticketPrices => this.updateTicketPrices(ticketPrices),
            error => this.presentFetchDataErrorToast()
          )
        }
      })
    }
  }
}
